import { Close } from '@mui/icons-material';
import { Dialog, IconButton, Box, SxProps, Typography } from '@mui/material';
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { borberBoxShadow } from '../constants/styles';
import useMedia from '../utils/useMedia';
import Confirmation from './Confirmation';
import Form from './forms/Form';
import Image from './Image';
import PrimaryButton, { PrimaryButtonProps } from './PrimaryButton';

type Props<T extends {}> = {
  open: boolean;
  title: string;
  imgUrl?: string;
  onClose: () => void;
  submitButton: string;
  submitButtonProps?: PrimaryButtonProps;
  onSubmit?: SubmitHandler<T>;
  secondaryButton?: string;
  secondaryButtonProps?: PrimaryButtonProps;
  onSecondaryButtonClick?: () => void;
  confirmText?: string;
  form: UseFormReturn<T>;
  stopPropagation?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  sx?: SxProps;
};

const EditModal = <T extends {}>(props: PropsWithChildren<Props<T>>) => {
  const {
    open,
    title,
    imgUrl,
    onClose,
    form,
    children,
    submitButton,
    submitButtonProps = {},
    onSubmit,
    secondaryButton,
    secondaryButtonProps = {},
    onSecondaryButtonClick,
    confirmText,
    size,
    sx,
    stopPropagation = true,
  } = props;

  const { isMobile } = useMedia();
  const ref = useRef<HTMLDivElement>(null);
  const [hasScroll, setHasScroll] = useState(false);
  const [mounted, setMounted] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const onConfirm = () => {
    onSecondaryButtonClick && onSecondaryButtonClick();
    setConfirmOpen(false);
    onClose();
  };

  const handleSubmit = useMemo(() => onSubmit && form.handleSubmit(onSubmit), [form, onSubmit]);

  useEffect(() => {
    if (mounted && ref.current && ref.current.scrollHeight !== ref.current?.clientHeight) {
      setHasScroll(true);
    }
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted, ref.current?.scrollHeight, ref.current?.clientHeight]);

  return (
    <>
      <Dialog
        sx={sx}
        onClose={onClose}
        open={open}
        fullWidth
        fullScreen={isMobile}
        maxWidth={size}
        PaperProps={{ ref }}
      >
        <div data-testid="modal-form">
          <Form form={form} onSubmit={onSubmit}>
            <Box
              sx={{
                minHeight: isMobile ? 'calc(100vh - 88px)' : undefined,
                position: 'relative',
              }}
              onClick={(e) => {
                if (stopPropagation) {
                  e.stopPropagation();
                }
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                py={2}
                px={3}
                position="sticky"
                top="0"
                zIndex={2}
                sx={{
                  backgroundColor: (t) => t.palette.background.paper,
                  boxShadow: hasScroll ? borberBoxShadow : undefined,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {imgUrl && <Image src={imgUrl} />}
                  <Typography variant="h6">{title}</Typography>
                </Box>
                <IconButton aria-label="close" onClick={onClose}>
                  <Close />
                </IconButton>
              </Box>

              <Box py={2} px={3}>
                {children}
              </Box>

              <Box
                display="flex"
                justifyContent={secondaryButton && !form.formState.isSubmitting ? 'space-between' : 'flex-end'}
                alignItems="center"
                p={3}
                position="sticky"
                bottom="0"
                left="0"
                right="0"
                zIndex={2}
                sx={{
                  backgroundColor: (t) => t.palette.background.paper,
                  boxShadow: hasScroll ? borberBoxShadow : undefined,
                }}
              >
                {secondaryButton && !form.formState.isSubmitting && (
                  <PrimaryButton
                    onClick={confirmText ? () => setConfirmOpen(true) : onSecondaryButtonClick}
                    color="error"
                    data-testid="form-secondary-button"
                    {...secondaryButtonProps}
                  >
                    {secondaryButton}
                  </PrimaryButton>
                )}
                {onSubmit && (
                  <PrimaryButton
                    variant="contained"
                    loading={form.formState.isSubmitting}
                    onClick={handleSubmit}
                    data-testid="form-primary-button"
                    {...submitButtonProps}
                  >
                    {submitButton}
                  </PrimaryButton>
                )}
              </Box>
            </Box>
          </Form>
        </div>
      </Dialog>

      {confirmText && secondaryButton && (
        <Confirmation
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          title={confirmText}
          onSubmit={onConfirm}
        />
      )}
    </>
  );
};

export default EditModal;
